import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import copy from 'copy-to-clipboard';
import * as actions from './CreateDashboardLink.ducks';

import Button from 'zignal-landscape/lib/Button';
import ButtonGroup from 'zignal-landscape/lib/ButtonGroup';
import Col from 'zignal-landscape/lib/Col';
import CopyIcon from 'zignal-landscape/lib/Icon/CopyIcon';
import DateRangePicker from 'zignal-landscape/lib/DateRangePicker';
import Field from 'zignal-landscape/lib/Field';
import InformationIcon from 'zignal-landscape/lib/Icon/InformationIcon';
import Input from 'zignal-landscape/lib/Input';
import Label from 'zignal-landscape/lib/Label';
import Loading from 'zignal-landscape/lib/Loading';
import Modal from 'zignal-landscape/lib/Modal';
import ModalHeader from 'zignal-landscape/lib/ModalHeader';
import ModalBody from 'zignal-landscape/lib/ModalBody';
import ModalFooter from 'zignal-landscape/lib/ModalFooter';
import Select from 'zignal-landscape/lib/Select';
import styles from './CreateDashboardLink.css';
import moment from 'moment';

// Be explicit of the state, not just return everything. This allows for
// clear understanding of what is needed if mixed with other state props.
const mapStateToProps = (
  (state) => {
    return {
      sharedToken: state.CreateDashboardLink.sharedToken,
      expiresIn: state.CreateDashboardLink.expiresIn,
      selectedStartDate: state.CreateDashboardLink.selectedStartDate,
      selectedEndDate: state.CreateDashboardLink.selectedEndDate,
      isModalOpen: state.CreateDashboardLink.isModalOpen,
      isCreatingDashboardLink: state.CreateDashboardLink.isCreatingDashboardLink,
      isLinkCopied: state.CreateDashboardLink.isLinkCopied,
      timezone: state.AppUser.user.timezone
    };
  }
);

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  return Object.assign({}, stateProps, dispatchProps, ownProps);
};

export class CreateDashboardLink extends Component {

  componentDidMount () {
    const { isModalOpen, setInitialState } = this.props;
    if (!isModalOpen) { setInitialState(); }
  }

  renderGeneratedLinkBodyStep () {
    const { isLinkCopied, setLinkCopied, sharedToken } = this.props;
    const copyToClipButtonText = isLinkCopied ? 'Copied to Clipboard' : 'Copy to Clipboard';

    return (
      <div>
        <p className={styles.ModalParagraphHeight}>
          Here is your generated link.
        </p>
        <Field>
          <Label>
            Copy this private URL to share:
          </Label>
          <div className={styles.LinkInputWrap}>
            <Input id="clipboard-to-copy" value={sharedToken.shortUrl} />
          </div>
          <div className={styles.CopyLinkButton}>
            <Button
              icon={<CopyIcon />}
              buttonStyle={isLinkCopied ? 'positive' : 'secondary'}
              onClick={() => {
                document.getElementById('clipboard-to-copy').select();
                copy(sharedToken.shortUrl);
                setLinkCopied();
              }}
            >{copyToClipButtonText}</Button>
          </div>
        </Field>
        <p className={styles.ModalParagraphHeight + ' ' + styles.InformationFontSize}>
          <InformationIcon /><i> Manage your generated links in Workspace Settings.</i>
        </p>
      </div>
    );
  }

  renderGeneratedLinkFooterStep () {
    const { closeModal } = this.props;
    return (
      <ButtonGroup align="right">
        <Button onClick={closeModal}><b>Done</b></Button>
      </ButtonGroup>
    );
  }

  renderCreateLinkBodyStep () {
    const { selectedStartDate, selectedEndDate, expiresIn, timeStartWindow,
      setExpiresIn, setDateRange, datePickerMinDate, timezone } = this.props;

    const expiresInOptions = [
      { value: 7, label: 'In 7 days' },
      { value: 30, label: 'In 30 days' }
    ];
    return (
      <form>
        <p className={styles.ModalParagraphHeight}>
          Edit viewing preferences before creating a custom generated link for this Dashboard.
        </p>
        <Field>
          <Label>Choose date range for the dashboard:</Label>
          <DateRangePicker
            timeStartWindow={timeStartWindow}
            minDate={datePickerMinDate}
            startDate={selectedStartDate}
            endDate={selectedEndDate}
            isOutsideRange={(day) => {
              const now = moment.tz(timezone);
              const isBeforeMinDate = day.isBefore(datePickerMinDate.startOf('day'));
              const isAfterMaxDate = day.isAfter(now.endOf('day'));
              return isBeforeMinDate || isAfterMaxDate;
            }}
            onChange={setDateRange}
            timezone={timezone}
          />
        </Field>
        <Col xs={3}>
          <Field>
            <Label>Access expires for users:</Label>
            <Select
              options={expiresInOptions}
              value={expiresIn}
              onChange={setExpiresIn}
            />
          </Field>
        </Col>
      </form>
    );
  }

  renderCreateLinkFooterStep () {
    const { closeModal, isCreatingDashboardLink, createDashboardLink,
      campaignId, createdBy, dashboardName, dashboardId,
      selectedStartDate, selectedEndDate } = this.props;
    return (
      <ButtonGroup align="right">
        <Button onClick={closeModal}>Cancel</Button>
        <Button
          buttonStyle="positive"
          onClick={() => {
            if (!isCreatingDashboardLink) {
              createDashboardLink(campaignId, createdBy, dashboardName, dashboardId);
            }
          }}
          disabled={(!selectedStartDate || !selectedEndDate)}
        >Next</Button>
      </ButtonGroup>
    );
  }

  render() {
    const { sharedToken, isModalOpen, closeModal, isCreatingDashboardLink } = this.props;

    const modalBody = sharedToken
      ? this.renderGeneratedLinkBodyStep()
      : this.renderCreateLinkBodyStep();

    const modalFooter = sharedToken
      ? this.renderGeneratedLinkFooterStep()
      : this.renderCreateLinkFooterStep();

    const loading = isCreatingDashboardLink
      ? <div className={styles.ModalLoadingOverlay}><Loading /></div>
      : null;

    return (
      <Modal isOpen={isModalOpen}>
        {loading}
        <ModalHeader
          hideCloseButton={!!sharedToken}
          onClose={closeModal}
        >Create Dashboard Link</ModalHeader>
        <div className={styles.ModalBodyMarginModifier}>
          <ModalBody>{modalBody}</ModalBody>
        </div>
        <ModalFooter>{modalFooter}</ModalFooter>
      </Modal>
    );
  }
}

CreateDashboardLink.propTypes = {
  campaignId: PropTypes.string,
  dashboardName: PropTypes.string,
  createdBy: PropTypes.string,
  dashboardId: PropTypes.string,
  selectedStartDate: PropTypes.string,
  selectedEndDate: PropTypes.string,
  expiresIn: PropTypes.object,
  sharedToken: PropTypes.object,
  isCreatingDashboardLink: PropTypes.bool,
  isLinkCopied: PropTypes.bool,
  isModalOpen: PropTypes.bool,
  createDashboardLink: PropTypes.func,
  closeModal: PropTypes.func,
  destroyHeaderCloseButton: PropTypes.func,
  setDateRange: PropTypes.func,
  datePickerMinDate: PropTypes.object,
  setExpiresIn: PropTypes.func,
  setInitialState: PropTypes.func,
  setLinkCopied: PropTypes.func,
  timezone: PropTypes.string,
  timeStartWindow: PropTypes.string
};

CreateDashboardLink.defaultProps = {
};

export default connect(mapStateToProps, actions, mergeProps)(CreateDashboardLink);
